import { SlideIn, StaggerContainer, StaggerItem, ViewportReveal, Float } from "@/components/motion";

const differentiators = [
  {
    title: "We Vet Rigorously",
    body: [
      "Only 3% of applicants join our network.",
      "Technical depth, communication, and commitment to learning are non‑negotiable.",
    ],
    items: [
      "Technical evaluation by senior practitioners",
      "Real-world case studies",
      "Communication and collaboration reviews",
      "Portfolio and experience verification",
    ],
  },
  {
    title: "We Train Continuously",
    body: [
      "Learn from Mark Kashef and industry experts through workshops, courses, and hands-on case studies.",
    ],
    items: [
      "12+ hours of live training monthly",
      "Access to proprietary course library",
      "Real-world project simulations",
      "Peer learning and knowledge sharing",
    ],
  },
  {
    title: "We Build Careers",
    body: [
      "Transparent progression, performance-based rate increases, and access to long-term consulting.",
    ],
    items: [
      "Skill progression framework",
      "Performance-based rate increases",
      "Priority access to consulting engagements",
      "Industry recognition and credibility boosts",
    ],
  },
];

export function DifferentiatorsSection() {
  return (
    <section
      id="experts"
      className="rounded-t-[80px] bg-[#2B42A3] px-6 sm:px-10 lg:px-16 xl:px-20 2k:px-64 4k:px-80 pt-16 sm:pt-20 lg:pt-24 2k:pt-28 4k:pt-32 text-white"
    >
      <ViewportReveal animation="fade" className="text-center">
        <p className="text-sm font-bold uppercase tracking-[1px] text-[#22D3EE]">
          What we do differently
        </p>
        <SlideIn direction="up" delay={0.1}>
          <h2 className="text-[56px] font-extrabold leading-tight mt-3">
            The Industry Is Changing.
            <br />
            We Change With It.
          </h2>
        </SlideIn>
        <SlideIn direction="up" delay={0.2}>
          <p className="w-2/3 text-lg text-[#CBD5E1] m-auto mt-6">
            Other platforms filter resumes and call it a day. We train, guide, and
            develop our experts continuously. Here&apos;s how we nurture your
            growth.
          </p>
        </SlideIn>
      </ViewportReveal>

      <StaggerContainer staggerDelay={0.1} delayChildren={0.3} viewport className="mt-20 mx-16 grid gap-5 lg:grid-cols-3">
        {differentiators.map((item, index) => (
          <StaggerItem
            key={item.title}
            className={`rounded-t-pill p-12 ${
              index === 1 ? "bg-[#4669ED] mt-0 pt-[68px]" : "bg-[#2D3B54] mt-5"
            }`}
          >
            <Float distance={6} duration={3} delay={index * 0.3}>
              <div className="rounded-[20px] bg-white/10 p-2 w-fit">
                {index === 0 && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="39"
                  height="39"
                  viewBox="0 0 39 39"
                  fill="none"
                >
                  <path
                    d="M19.5 35.75C28.4746 35.75 35.75 28.4746 35.75 19.5C35.75 10.5254 28.4746 3.25 19.5 3.25C10.5254 3.25 3.25 10.5254 3.25 19.5C3.25 28.4746 10.5254 35.75 19.5 35.75Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19.5 29.25C24.8848 29.25 29.25 24.8848 29.25 19.5C29.25 14.1152 24.8848 9.75 19.5 9.75C14.1152 9.75 9.75 14.1152 9.75 19.5C9.75 24.8848 14.1152 29.25 19.5 29.25Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19.5 22.75C21.2949 22.75 22.75 21.2949 22.75 19.5C22.75 17.7051 21.2949 16.25 19.5 16.25C17.7051 16.25 16.25 17.7051 16.25 19.5C16.25 21.2949 17.7051 22.75 19.5 22.75Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {index === 1 && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="41"
                  height="40"
                  viewBox="0 0 41 40"
                  fill="none"
                >
                  <path
                    d="M36.5925 18.2036C36.8983 18.072 37.1579 17.8557 37.3389 17.5816C37.52 17.3075 37.6147 16.9876 37.6112 16.6615C37.6078 16.3354 37.5064 16.0175 37.3195 15.7471C37.1327 15.4767 36.8686 15.2657 36.5601 15.1403L21.9179 8.6336C21.4728 8.43552 20.9893 8.33301 20.5 8.33301C20.0108 8.33301 19.5272 8.43552 19.0821 8.6336L4.44169 15.1336C4.13755 15.2636 3.87882 15.4772 3.69714 15.7483C3.51546 16.0194 3.4187 16.3363 3.4187 16.6603C3.4187 16.9842 3.51546 17.3011 3.69714 17.5722C3.87882 17.8434 4.13755 18.057 4.44169 18.1869L19.0821 24.7003C19.5272 24.8984 20.0108 25.0009 20.5 25.0009C20.9893 25.0009 21.4728 24.8984 21.9179 24.7003L36.5925 18.2036Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M37.5833 16.667V26.667"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.25 20.833V26.6663C10.25 27.9924 11.3299 29.2642 13.2522 30.2019C15.1744 31.1396 17.7815 31.6663 20.5 31.6663C23.2185 31.6663 25.8256 31.1396 27.7478 30.2019C29.6701 29.2642 30.75 27.9924 30.75 26.6663V20.833"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {index === 2 && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="42"
                  height="41"
                  viewBox="0 0 42 41"
                  fill="none"
                >
                  <path
                    d="M5.25 5.125V32.4583C5.25 33.3645 5.61875 34.2335 6.27513 34.8743C6.9315 35.515 7.82174 35.875 8.75 35.875H36.75"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.25 19.145C12.25 18.9189 12.3419 18.702 12.5055 18.542L16.0055 15.1253C16.0868 15.0457 16.1833 14.9826 16.2896 14.9396C16.3959 14.8965 16.5099 14.8743 16.625 14.8743C16.7401 14.8743 16.8541 14.8965 16.9604 14.9396C17.0667 14.9826 17.1632 15.0457 17.2445 15.1253L23.0055 20.7491C23.0868 20.8287 23.1833 20.8918 23.2896 20.9348C23.3959 20.9779 23.5099 21.0001 23.625 21.0001C23.7401 21.0001 23.8541 20.9779 23.9604 20.9348C24.0667 20.8918 24.1632 20.8287 24.2445 20.7491L31.7555 13.417C31.8778 13.2973 32.0337 13.2157 32.2034 13.1826C32.3732 13.1495 32.5492 13.1662 32.7092 13.2308C32.8693 13.2954 33.006 13.4048 33.1023 13.5453C33.1986 13.6858 33.25 13.851 33.25 14.02V27.333C33.25 27.7861 33.0656 28.2206 32.7374 28.541C32.4092 28.8614 31.9641 29.0414 31.5 29.0414H14C13.5359 29.0414 13.0908 28.8614 12.7626 28.541C12.4344 28.2206 12.25 27.7861 12.25 27.333V19.145Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              </div>
            </Float>
            <h3 className="mt-8 text-3xl font-bold">{item.title}</h3>
            <p className="mt-4 text-base font-normal text-[#CBD5E1]">
              {item.body.join(" ")}
            </p>
            <ul className="mt-6 space-y-3 text-base text-[#CBD5E1]">
              {item.items.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span
                    className={`text-[18px] font-bold ${
                      index === 1 ? "text-white" : "text-[#22D3EE]"
                    }`}
                  >
                    →
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </section>
  );
}
